import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  deleteListing,
  deleteArchivedListing,
  toggleListingSoldStatus,
  reactivateListing,
  deactivateListing,
  restoreArchivedListing,
} from "@/lib/actions/listingActions";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { commonToasts } from "@/hooks/toast/toast-translations";
import {
  getUserPremiumVouchers,
  usePremiumVoucher,
} from "@/lib/actions/premiumActions";

// This interface should align with the relevant parts of get_user_credits RPC output
interface UserCreditsRpcData {
  free_listings_remaining: number;
  purchased_listings_credits: number;
  // Add other fields if needed by this hook, matching RPC output
  // default_free_listings, free_stocks_remaining etc.
}


// Simulated PayPal payment process.
// Replace this function with your actual PayPal integration logic.
async function processPayPalPayment(
  listingId: number
): Promise<{ success: boolean; error?: string }> {
  return new Promise((resolve) => {
    setTimeout(() => {
      const paySuccess = true; // Simulate a successful payment
      resolve({ success: paySuccess });
    }, 1000);
  });
}

interface UseListingActionsProps {
  userId: string;
}

export const useListingActions = ({ userId }: UseListingActionsProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedListingId, setSelectedListingId] = useState<number | null>(
    null
  );
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogAction, setConfirmDialogAction] = useState<
    "delete" | "sold" | "deactivate" | null
  >(null);
  const [premiumDialogOpen, setPremiumDialogOpen] = useState(false);
  const [premiumConfirmDialogOpen, setPremiumConfirmDialogOpen] =
    useState(false);
  const [isArchived, setIsArchived] = useState(false);
  const [archiveRestoreDialogOpen, setArchiveRestoreDialogOpen] = useState(false);
  const [originalIdToRestore, setOriginalIdToRestore] = useState<number | null>(null);


  const {
    data: premiumVouchersCount = 0,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["premiumVouchers", userId],
    queryFn: async () => {
      if (!userId) return 0;
      const result = await getUserPremiumVouchers(userId);
      return result;
    },
    enabled: !!userId,
  });

  const { data: userCredits, isLoading: isLoadingUserCredits } = useQuery<UserCreditsRpcData | null>({
    queryKey: ["userCreditsRpcData", userId], // Use the same key as other hooks fetching this RPC
    queryFn: async () => {
      if (!userId) return null;
      
      const { data, error: rpcError } = await supabase.rpc('get_user_credits', {
        p_user_id: userId,
      });
      
      if (rpcError) {
        console.error("useListingActions: Error fetching user credits via RPC:", rpcError);
        // Consider throwing error or returning a more specific error state
        return null; 
      }
      
      const rpcData = data?.[0];
      if (!rpcData) {
        console.warn("useListingActions: RPC get_user_credits returned no data for user", userId);
        return null;
      }

      return {
        free_listings_remaining: rpcData.free_listings_remaining ?? 0,
        purchased_listings_credits: rpcData.purchased_listings_credits ?? 0,
        // Map other fields from rpcData if UserCreditsRpcData expects them
      };
    },
    enabled: !!userId,
  });

  const hasAvailableCredits = () => {
    if (isLoadingUserCredits || !userCredits) return false;
    return (userCredits.purchased_listings_credits > 0 || userCredits.free_listings_remaining > 0);
  };

  const deleteMutation = useMutation({
    mutationFn: async (listingId: number) => {
      let result;
      if (isArchived) {
        result = await deleteArchivedListing(listingId, userId);
      } else {
        result = await deleteListing(listingId, userId);
      }
      if (!result.success) {
        throw new Error(result.error);
      }
      return listingId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userListings", userId] });
      queryClient.invalidateQueries({ queryKey: ["archivedListings", userId] });
      toast(commonToasts.listingDeleted);
      setConfirmDialogOpen(false);
    },
    onError: (error: any) => {
      setConfirmDialogOpen(false);
      if (error.message === "listing_has_active_orders") {
        toast(commonToasts.listingDeleteBlocked);
      } else {
        toast({
          title: "Error deleting listing",
          description:
            error.message || "An error occurred while deleting your listing.",
          variant: "destructive",
        });
      }
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: async (listingId: number) => {
      const result = await deactivateListing(listingId, userId);
      if (!result.success)
        throw new Error(result.error || "Error deactivating listing");
      return listingId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userListings", userId] });
      toast(commonToasts.listingDeactivated);
      setConfirmDialogOpen(false);
    },
    onError: (error: any) => {
      setConfirmDialogOpen(false);
      toast({
        title: "Error deactivating listing",
        description:
          error.message || "An error occurred while deactivating your listing.",
        variant: "destructive",
      });
    },
  });

  const togglePremiumMutation = useMutation({
    mutationFn: async ({
      id,
      isPremium,
    }: {
      id: number;
      isPremium: boolean;
    }) => {
      if (isPremium) {
        const { error } = await supabase
          .from("listings")
          .update({ is_premium: !isPremium })
          .eq("id", id);
        if (error) throw error;
        return id;
      }
      if (premiumVouchersCount > 0) {
        const success = await usePremiumVoucher(id, userId);
        if (!success) {
          throw new Error("Failed to use premium voucher");
        }
        return id;
      }
      const paymentResult = await processPayPalPayment(id);
      if (!paymentResult.success) {
        throw new Error(paymentResult.error || "PayPal payment failed");
      }
      const { error } = await supabase
        .from("listings")
        .update({ is_premium: true })
        .eq("id", id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userListings", userId] });
      queryClient.invalidateQueries({ queryKey: ["premiumVouchers", userId] });
      toast({
        title: "Statusi Premium u përditësua",
        description: "Statusi premium i shpalljes tuaj është përditësuar.",
      });
      setPremiumConfirmDialogOpen(false);
      setPremiumDialogOpen(false);
    },
    onError: (error: any) => {
      setPremiumConfirmDialogOpen(false);
      setPremiumDialogOpen(false);
      toast({
        title: "Gabim në përditësimin e statusit premium",
        description:
          error.message ||
          "Ndodhi një gabim gjatë përditësimit të statusit premium të shpalljes tuaj.",
        variant: "destructive",
      });
    },
  });

  const toggleSoldMutation = useMutation({
    mutationFn: async ({ id, isSold }: { id: number; isSold: boolean }) => {
      const result = await toggleListingSoldStatus(id, isSold);
      if (!result.success)
        throw new Error(
          result.error || "Error updating sold status of listing"
        );
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userListings", userId] });
      toast({
        title: "Statusi i shitjes u përditësua",
        description: "Statusi i shitjes së shpalljes tuaj është përditësuar.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Gabim në përditësimin e statusit të shitjes",
        description:
          error.message ||
          "Ndodhi një gabim gjatë përditësimit të statusit të shitjes së shpalljes tuaj.",
        variant: "destructive",
      });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: async (listingId: number) => {
      const result = await reactivateListing(listingId, userId); 
      if (!result.success) {
        throw new Error(result.error || "Error reactivating listing");
      }
      return listingId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userListings", userId] });
      queryClient.invalidateQueries({ queryKey: ["userCreditsRpcData", userId] }); 
      toast(commonToasts.listingActivated);
    },
    onError: (error: any) => {
      toast({
        title: "Gabim gjatë riaktivizimit",
        description:
          error.message || "Ndodhi një gabim gjatë riaktivizimit të shpalljes tuaj.",
        variant: "destructive",
      });
    },
  });

  const restoreArchivedMutation = useMutation({
    mutationFn: async ({ originalId }: { originalId: number }) => {
      const result = await restoreArchivedListing(originalId, userId);
      if (!result.success) {
        throw new Error(result.error || "Error restoring archived listing");
      }
      return result; 
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userListings", userId] });
      queryClient.invalidateQueries({ queryKey: ["archivedListings", userId] });
      queryClient.invalidateQueries({ queryKey: ["userCreditsRpcData", userId] });
      toast(commonToasts.listingActivated);
      setArchiveRestoreDialogOpen(false);
    },
    onError: (error: any) => {
      setArchiveRestoreDialogOpen(false);
      if (error.message === "No credits available to restore this archived listing" || 
          error.message.includes("Kredite të pamjaftueshme") || 
          error.message.includes("Failed to deduct credit")) {
        toast({
          title: "Kredite të pamjaftueshme",
          description: "Ju nevojitet një kredit për të riaktivizuar një shpallje të arkivuar ose gabim gjate zbritjes se krediteve.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Gabim gjatë riaktivizimit",
          description:
            error.message || "Ndodhi një gabim gjatë riaktivizimit të shpalljes tuaj.",
          variant: "destructive",
        });
      }
    },
  });
  const handleDeleteListing = (
    listingId: number,
    isArchivedListing = false
  ) => {
    setSelectedListingId(listingId);
    setIsArchived(isArchivedListing);
    setConfirmDialogAction("delete");
    setConfirmDialogOpen(true);
  };

  const handleDeactivateListing = (listingId: number) => {
    setSelectedListingId(listingId);
    setConfirmDialogAction("deactivate");
    setConfirmDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedListingId) {
      deleteMutation.mutate(selectedListingId);
    }
  };

  const confirmDeactivate = () => {
    if (selectedListingId) {
      deactivateMutation.mutate(selectedListingId);
    }
  };

  const handleTogglePremium = (id: number, isPremium: boolean) => {
    if (isPremium) {
      togglePremiumMutation.mutate({ id, isPremium });
    } else if (premiumVouchersCount > 0) {
      setSelectedListingId(id);
      setPremiumConfirmDialogOpen(true);
    } else {
      setSelectedListingId(id);
      setPremiumDialogOpen(true);
    }
  };

  const confirmPremiumUpgrade = () => {
    if (selectedListingId !== null) {
      togglePremiumMutation.mutate({ id: selectedListingId, isPremium: false });
    }
  };

  const handlePremiumSuccess = () => {
    setPremiumDialogOpen(false);
    queryClient.invalidateQueries({ queryKey: ["userListings", userId] });
    queryClient.invalidateQueries({ queryKey: ["premiumVouchers", userId] });
    toast({
      title: "Premium u aktivizua",
      description: "Shpallja juaj u ngrit në premium.",
    });
  };

  const handleToggleSold = (id: number, isSold: boolean) => {
    setSelectedListingId(id);
    // Ensure action is set if needed, or simplify if confirmToggleSold directly handles it
    // setConfirmDialogAction("sold"); 
    // setConfirmDialogOpen(true); // Original had this, re-evaluate if needed
    // For immediate toggle without confirmation dialog (if desired)
    toggleSoldMutation.mutate({ id: id, isSold: isSold });
  };

  const confirmToggleSold = () => {
    // This function might be redundant if handleToggleSold mutates directly
    // Or it's used by a confirmation dialog for "Mark as Sold"
    if (selectedListingId !== null && confirmDialogAction === 'sold') {
      // Assuming isSold should be true when confirming "Mark as Sold"
      toggleSoldMutation.mutate({
        id: selectedListingId,
        isSold: true, 
      });
    }
    setConfirmDialogOpen(false);
  };
  
  const handleReactivateListing = (listingId: number, isFromArchive: boolean = false, originalId?: number) => {
    if (isFromArchive && originalId) {
      if (!hasAvailableCredits()) {
        toast({
          title: "Kredite të pamjaftueshme",
          description: "Ju nevojitet një kredit për të riaktivizuar një shpallje të arkivuar.",
          variant: "destructive",
        });
        return;
      }
      setOriginalIdToRestore(originalId);
      setArchiveRestoreDialogOpen(true);
    } else if (listingId > 0) { 
        if (!hasAvailableCredits()) {
            toast({
                title: "Kredite të pamjaftueshme",
                description: "Ju nevojitet një kredit për të riaktivizuar këtë shpallje.",
                variant: "destructive",
            });
            return;
        }
        reactivateMutation.mutate(listingId);
    } else {
      // This case seems unlikely if listingId is expected to be > 0 for regular inactive.
      // If it's a general refresh, invalidate queries.
      queryClient.invalidateQueries({ queryKey: ["userListings", userId] });
      queryClient.invalidateQueries({ queryKey: ["archivedListings", userId] });
    }
  };

  const confirmRestoreArchived = () => {
    if (originalIdToRestore) {
      restoreArchivedMutation.mutate({ originalId: originalIdToRestore });
    }
  };


  return {
    selectedListingId,
    confirmDialogOpen,
    confirmDialogAction,
    premiumDialogOpen,
    premiumConfirmDialogOpen,
    premiumVouchersCount,
    archiveRestoreDialogOpen,
    setArchiveRestoreDialogOpen,
    hasAvailableCredits,
    setConfirmDialogOpen,
    setPremiumDialogOpen,
    setPremiumConfirmDialogOpen,
    handleDeleteListing,
    handleDeactivateListing,
    confirmDelete,
    confirmDeactivate,
    handleTogglePremium,
    confirmPremiumUpgrade,
    handlePremiumSuccess,
    handleToggleSold,
    confirmToggleSold,
    handleReactivateListing,
    confirmRestoreArchived,
    // Expose mutations if needed by the component
    // deleteMutation, reactivateMutation, etc.
  };
};
